import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  Clock,
  Calendar,
  Ticket,
  Users,
  Check,
  X,
  Eye,
  BookOpen,
  Search,
  FileText,
  Building,
  ArrowRight,
  Info
} from 'lucide-react';
import { ExaminationPassModal } from '../../components/admissions/EnglishAdmissionsDocuments';
import './AdmissionsFeeApprovals.css';

const LEVEL_META = {
  preschool: { title: 'Preschool (KB / Playgroup)', tag: 'KB', color: '#10b981' },
  kindergarten: { title: 'Kindergarten (TK A & TK B)', tag: 'TK', color: '#06b6d4' },
  primary: { title: 'Primary School (SD Kelas 1 - 6)', tag: 'SD', color: '#3b82f6' },
  secondary: { title: 'Secondary School (SMP Kelas 7 - 9)', tag: 'SMP', color: '#8b5cf6' },
  sma: { title: 'High School (SMA - Coming Soon)', tag: 'SMA', color: '#f59e0b' },
};

function formatRupiah(num) {
  const n = Number(num || 0);
  return 'Rp ' + n.toLocaleString('id-ID');
}

function formatDateDisplay(dStr) {
  if (!dStr) return '-';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return dStr;
  }
}

function formatDateId(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return dateStr;
  }
}

const CATEGORY_DETAILS = {
  levels: {
    breadcrumb: 'KEBIJAKAN & APPROVAL / STRUKTUR BIAYA JENJANG',
    title: 'Pusat Persetujuan Struktur Biaya Masuk & Tarif Jenjang',
    subtitle: 'Otoritas tunggal Direktur Sekolah (School Director) untuk memverifikasi dan mengesahkan Tarif Formulir, Uang Masuk, Uang Pangkal (Termin DF 1-4), SPP Bulanan, dan rekening pembayaran resmi tiap jenjang sebelum ditagihkan ke orang tua calon siswa.',
    icon: CreditCard,
    roleTitle: 'School Director • Fee Governance',
    itemName: 'Jenjang Pendidikan',
  },
  periods: {
    breadcrumb: 'KEBIJAKAN & APPROVAL / PERIODE & KUOTA',
    title: 'Pusat Persetujuan Gelombang Periode Pendaftaran & Kuota',
    subtitle: 'Otoritas tunggal Direktur Sekolah (School Director) untuk mengesahkan pembukaan gelombang baru, tanggal buka/tutup pendaftaran, dan alokasi kuota kursi siswa tiap jenjang.',
    icon: Calendar,
    roleTitle: 'School Director • Period Governance',
    itemName: 'Gelombang Pendaftaran',
  },
  promo_codes: {
    breadcrumb: 'KEBIJAKAN & APPROVAL / KODE PROMO & DISKON',
    title: 'Pusat Persetujuan Kode Voucher & Diskon PPDB',
    subtitle: 'Otoritas tunggal Direktur Sekolah (School Director) untuk mengesahkan kode promo diskon uang pangkal / open house sebelum dapat diverifikasi dan digunakan orang tua siswa.',
    icon: Ticket,
    roleTitle: 'School Director • Discount Governance',
    itemName: 'Kode Voucher Promo',
  },
  test_slots: {
    breadcrumb: 'KEBIJAKAN & APPROVAL / JADWAL SESI TES',
    title: 'Pusat Persetujuan Jadwal Sesi Tes Masuk & Observasi',
    subtitle: 'Otoritas Kepala Sekolah (Principal) untuk mengesahkan tanggal pelaksanaan, waktu, ruangan, dan kapasitas peserta tes offline.',
    icon: Clock,
    roleTitle: 'School Principal • Test Slots',
    itemName: 'Sesi Tes Offline',
  },
  assessment: {
    breadcrumb: 'KEBIJAKAN & APPROVAL / MATERI ASESMEN & KARTU TES',
    title: 'Pusat Persetujuan Materi Asesmen & Tata Tertib Tes Masuk',
    subtitle: 'Otoritas Kepala Sekolah (Principal) untuk mengesahkan judul observasi, silabus materi asesmen, perlengkapan siswa, dan tata tertib resmi yang tercetak pada Kartu Peserta Tes.',
    icon: BookOpen,
    roleTitle: 'School Principal • Assessment Governance',
    itemName: 'Kurikulum Asesmen',
  },
};

export default function AdmissionsFeeApprovals({ apiBase, onFeeChange, initialCategory = 'levels', userRole = 'director' }) {
  const [activeCategory, setActiveCategory] = useState(initialCategory || 'levels');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'approved'
  const [searchQuery, setSearchQuery] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const activeTestingRole = localStorage.getItem('active_testing_role');
    headers['X-Active-Role'] = activeTestingRole || userRole || 'director';
    return headers;
  }, [userRole]);

  useEffect(() => {
    if (initialCategory) {
      setActiveCategory(initialCategory);
    }
  }, [initialCategory]);

  const [levels, setLevels] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [testSlots, setTestSlots] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [configApprovals, setConfigApprovals] = useState([]);
  const [applications, setApplications] = useState([]);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Detail Modal State (Read-only)
  const [detailModalItem, setDetailModalItem] = useState(null);
  const [detailModalType, setDetailModalType] = useState('level'); // 'level' | 'period' | 'slot' | 'promo' | 'assessment'
  const [feeModalTab, setFeeModalTab] = useState('fees'); // 'fees' | 'bank_sign'

  // Rejection Modals
  const [rejectModalLevel, setRejectModalLevel] = useState(null);
  const [rejectModalConfig, setRejectModalConfig] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  // Pass preview modal for Principal
  const [passPreviewModal, setPassPreviewModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const headers = getAuthHeaders();
      const [resOverview, resLevels, resConfigs, resApps] = await Promise.all([
        fetch(`${apiBase}/admin/admissions/overview?_t=${Date.now()}`, {
          credentials: 'include',
          headers,
        }),
        fetch(`${apiBase}/admin/admissions/levels?_t=${Date.now()}`, {
          credentials: 'include',
          headers,
        }),
        fetch(`${apiBase}/admin/admissions/config-approvals?_t=${Date.now()}`, {
          credentials: 'include',
          headers,
        }),
        fetch(`${apiBase}/admin/admissions/applications?_t=${Date.now()}`, {
          credentials: 'include',
          headers,
        }).catch(() => null),
      ]);

      if (resOverview && resOverview.ok) {
        const ovData = await resOverview.json();
        if (ovData?.ok && ovData?.data) {
          setPeriods(ovData.data.periods || []);
          setTestSlots(ovData.data.test_slots || []);
          setPromoCodes(ovData.data.promo_codes || []);
        }
      }

      if (resLevels && resLevels.ok) {
        const dataLevels = await resLevels.json();
        if (dataLevels?.ok && Array.isArray(dataLevels.data)) {
          setLevels(dataLevels.data);
        }
      }

      if (resConfigs && resConfigs.ok) {
        const dataConfigs = await resConfigs.json();
        if (dataConfigs?.ok && Array.isArray(dataConfigs.data)) {
          setConfigApprovals(dataConfigs.data);
        }
      }

      if (resApps && resApps.ok) {
        const dataApps = await resApps.json();
        if (dataApps?.ok && Array.isArray(dataApps.data)) {
          setApplications(dataApps.data);
        }
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Gagal terhubung ke server untuk memuat data pengajuan approval' });
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 1. Level Fee Decision
  const handleApproveLevelFee = async (levelCode) => {
    if (!window.confirm(`Konfirmasi: Setujui struktur biaya resmi baru untuk jenjang ${levelCode.toUpperCase()}?`)) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${levelCode}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ decision: 'approve', note: 'Disetujui oleh Direktur Sekolah (School Director)' }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Biaya jenjang berhasil disetujui dan kini aktif!' });
        setDetailModalItem(null);
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menyetujui biaya jenjang' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses approval' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRejectLevel = async (e) => {
    e.preventDefault();
    if (!rejectModalLevel) return;
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/levels/${rejectModalLevel.level_code}/approve-fees`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ decision: 'reject', note: rejectNote || 'Perubahan biaya ditolak oleh Direktur Sekolah (School Director).' }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'info', text: 'Pengajuan perubahan biaya telah ditolak dan dikembalikan ke Admin Admissions.' });
        setRejectModalLevel(null);
        setDetailModalItem(null);
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal menolak perubahan biaya' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses penolakan' });
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Config Approvals Decision (Periods, Slots, Promos, Assessment)
  const handleConfigDecision = async (approvalId, decision, note = '') => {
    if (decision === 'approve' && !window.confirm('Konfirmasi: Setujui pengajuan ini dan terapkan langsung di sistem?')) {
      return;
    }
    setActionLoading(true);
    setFeedback(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/config-approvals/${approvalId}/decision`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ decision, note }),
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        setFeedback({ type: 'success', text: data.message || 'Pengajuan berhasil diproses' });
        setDetailModalItem(null);
        await loadData();
        if (onFeeChange) onFeeChange();
      } else {
        setFeedback({ type: 'error', text: data.message || 'Gagal memproses approval' });
      }
    } catch (_) {
      setFeedback({ type: 'error', text: 'Terjadi kesalahan jaringan saat memproses pengajuan' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmRejectConfig = async (e) => {
    e.preventDefault();
    if (!rejectModalConfig) return;
    await handleConfigDecision(rejectModalConfig.id, 'reject', rejectNote || 'Ditolak oleh pimpinan sekolah.');
    setRejectModalConfig(null);
  };

  // Build merged items for tables
  const pendingApprovals = configApprovals.filter(c => c.status === 'pending');

  // 1. Periods list merged
  const periodPendingTickets = pendingApprovals.filter(c => c.config_type === 'period');
  const mergedPeriods = (() => {
    const list = [];
    // 1. Pending create tickets
    periodPendingTickets.filter(t => t.action_type === 'create').forEach(t => {
      list.push({
        id: `pending_create_${t.id}`,
        is_pending_approval: true,
        action_type: 'create',
        pending_ticket: t,
        period_name: t.payload?.period_name || t.target_label?.replace('Tambah Periode: ', '') || 'Gelombang Baru (Pengajuan)',
        academic_year: t.payload?.academic_year || '2026/2027',
        start_date: t.payload?.start_date,
        end_date: t.payload?.end_date,
        quotas: t.payload?.quotas || {},
        is_active: t.payload?.is_active ?? true,
        notes: t.payload?.notes || '',
        requested_by_name: t.requested_by_name,
        created_at: t.created_at,
      });
    });
    // 2. Existing periods (with update/delete ticket flags if any)
    periods.forEach(p => {
      const updateTicket = periodPendingTickets.find(t => t.action_type === 'update' && (t.target_id === p.id || t.target_label?.includes(p.period_name)));
      const deleteTicket = periodPendingTickets.find(t => t.action_type === 'delete' && (t.target_id === p.id || t.target_label?.includes(p.period_name)));
      list.push({
        ...p,
        is_pending_approval: Boolean(updateTicket || deleteTicket),
        action_type: updateTicket ? 'update' : (deleteTicket ? 'delete' : null),
        pending_ticket: updateTicket || deleteTicket || null,
        requested_by_name: (updateTicket || deleteTicket)?.requested_by_name,
        created_at: (updateTicket || deleteTicket)?.created_at,
      });
    });
    return list;
  })();

  // 2. Test Slots merged
  const slotPendingTickets = pendingApprovals.filter(c => c.config_type === 'test_slot');
  const mergedSlots = (() => {
    const list = [];
    slotPendingTickets.filter(t => t.action_type === 'create').forEach(t => {
      list.push({
        id: `pending_create_${t.id}`,
        is_pending_approval: true,
        action_type: 'create',
        pending_ticket: t,
        session_name: t.payload?.session_name || t.target_label?.replace('Tambah Sesi: ', '') || 'Sesi Tes Baru (Pengajuan)',
        level_code: t.payload?.level_code || 'all',
        test_date: t.payload?.test_date,
        test_time: t.payload?.test_time,
        campus_location: t.payload?.campus_location || 'Campus 1 (Pos Pengumben)',
        room_name: t.payload?.room_name || 'Ruang Assessment',
        slot_quota: t.payload?.slot_quota || 15,
        booked_count: 0,
        custom_notes: t.payload?.custom_notes || '',
        requested_by_name: t.requested_by_name,
        created_at: t.created_at,
      });
    });
    testSlots.forEach(s => {
      const updateTicket = slotPendingTickets.find(t => t.action_type === 'update' && (t.target_id === s.id || t.target_label?.includes(s.session_name)));
      const deleteTicket = slotPendingTickets.find(t => t.action_type === 'delete' && (t.target_id === s.id || t.target_label?.includes(s.session_name)));
      list.push({
        ...s,
        is_pending_approval: Boolean(updateTicket || deleteTicket),
        action_type: updateTicket ? 'update' : (deleteTicket ? 'delete' : null),
        pending_ticket: updateTicket || deleteTicket || null,
        requested_by_name: (updateTicket || deleteTicket)?.requested_by_name,
        created_at: (updateTicket || deleteTicket)?.created_at,
      });
    });
    return list;
  })();

  // 3. Promo Codes merged
  const promoPendingTickets = pendingApprovals.filter(c => c.config_type === 'promo_code');
  const mergedPromos = (() => {
    const list = [];
    promoPendingTickets.filter(t => t.action_type === 'create').forEach(t => {
      list.push({
        id: `pending_create_${t.id}`,
        is_pending_approval: true,
        action_type: 'create',
        pending_ticket: t,
        promo_code: t.payload?.promo_code || 'PROMO-BARU',
        promo_title: t.payload?.promo_title || t.target_label?.replace('Tambah Kode: ', ''),
        discount_type: t.payload?.discount_type || 'fixed',
        discount_value: t.payload?.discount_value || 0,
        applies_to_levels: t.payload?.applies_to_levels || 'all',
        valid_until: t.payload?.valid_until || '2027-06-30',
        max_quota: t.payload?.max_quota || 100,
        used_count: 0,
        requested_by_name: t.requested_by_name,
        created_at: t.created_at,
      });
    });
    promoCodes.forEach(pr => {
      const updateTicket = promoPendingTickets.find(t => t.action_type === 'update' && (t.target_id === pr.id || t.target_label?.includes(pr.promo_code)));
      const deleteTicket = promoPendingTickets.find(t => t.action_type === 'delete' && (t.target_id === pr.id || t.target_label?.includes(pr.promo_code)));
      list.push({
        ...pr,
        is_pending_approval: Boolean(updateTicket || deleteTicket),
        action_type: updateTicket ? 'update' : (deleteTicket ? 'delete' : null),
        pending_ticket: updateTicket || deleteTicket || null,
        requested_by_name: (updateTicket || deleteTicket)?.requested_by_name,
        created_at: (updateTicket || deleteTicket)?.created_at,
      });
    });
    return list;
  })();

  // 4. Assessment Levels merged
  const assessmentPendingTickets = pendingApprovals.filter(c => c.config_type === 'assessment');
  const mergedAssessments = levels.map(lvl => {
    const ticket = assessmentPendingTickets.find(t => t.target_id === lvl.id || t.target_label?.toLowerCase().includes(lvl.level_name.toLowerCase()));
    const isPending = lvl.assessment_approval_status === 'pending_principal' || Boolean(ticket) || Boolean(lvl.pending_assessment_payload);
    return {
      ...lvl,
      is_pending_approval: isPending,
      pending_ticket: ticket || null,
      requested_by_name: ticket?.requested_by_name || 'Admin Admissions',
      created_at: ticket?.created_at || lvl.updated_at,
    };
  });

  // KPI Calculations
  const categoryMeta = CATEGORY_DETAILS[activeCategory] || CATEGORY_DETAILS.levels;

  const currentPendingCount = activeCategory === 'levels'
    ? levels.filter(l => l.fee_approval_status === 'pending_director' || l.fee_approval_status === 'pending_principal' || Boolean(l.pending_fee_payload)).length
    : activeCategory === 'periods'
    ? mergedPeriods.filter(p => p.is_pending_approval).length
    : activeCategory === 'promo_codes'
    ? mergedPromos.filter(pr => pr.is_pending_approval).length
    : activeCategory === 'test_slots'
    ? mergedSlots.filter(s => s.is_pending_approval).length
    : mergedAssessments.filter(a => a.is_pending_approval).length;

  const totalItemCount = activeCategory === 'levels'
    ? levels.length
    : activeCategory === 'periods'
    ? mergedPeriods.length
    : activeCategory === 'promo_codes'
    ? mergedPromos.length
    : activeCategory === 'test_slots'
    ? mergedSlots.length
    : mergedAssessments.length;

  const approvedItemCount = Math.max(0, totalItemCount - currentPendingCount);

  // Filter and Search logic
  const filterList = (items, searchFieldExtractor) => {
    return items.filter(item => {
      const isPending = item.is_pending_approval || (activeCategory === 'levels' && (item.fee_approval_status === 'pending_director' || item.fee_approval_status === 'pending_principal' || Boolean(item.pending_fee_payload)));
      if (statusFilter === 'pending' && !isPending) return false;
      if (statusFilter === 'approved' && isPending) return false;
      if (searchQuery.trim()) {
        const text = searchFieldExtractor(item).toLowerCase();
        if (!text.includes(searchQuery.toLowerCase())) return false;
      }
      return true;
    });
  };

  const filteredLevels = filterList(levels, l => `${l.level_name} ${l.level_code} ${l.assessment_title}`);
  const filteredPeriods = filterList(mergedPeriods, p => `${p.period_name} ${p.academic_year} ${p.notes}`);
  const filteredSlots = filterList(mergedSlots, s => `${s.session_name} ${s.campus_location} ${s.room_name} ${s.level_code}`);
  const filteredPromos = filterList(mergedPromos, pr => `${pr.promo_code} ${pr.promo_title}`);
  const filteredAssessments = filterList(mergedAssessments, a => `${a.level_name} ${a.assessment_title} ${a.assessment_subjects}`);

  // Action Handlers
  const handleOpenDetail = (item, type) => {
    setDetailModalItem(item);
    setDetailModalType(type);
    setFeeModalTab('fees');
  };

  const handleOpenRejectModal = (item, type) => {
    setRejectNote('');
    if (type === 'levels') {
      setRejectModalLevel(item);
    } else {
      const ticket = item.pending_ticket || item;
      setRejectModalConfig(ticket);
    }
  };

  const handleDirectApprove = (item, type) => {
    if (type === 'levels') {
      handleApproveLevelFee(item.level_code);
    } else {
      const ticketId = item.pending_ticket ? item.pending_ticket.id : item.id;
      handleConfigDecision(ticketId, 'approve');
    }
  };

  const handlePreviewPass = (lvl) => {
    setPassPreviewModal({
      id: 9999,
      registration_no: 'REG-PHS-PREVIEW',
      child_full_name: 'Muhammad Farhan',
      grade_target: lvl.level_name,
      level_code: lvl.level_code,
      academic_year: '2026/2027',
      birth_date: '2014-05-18',
      gender: 'male',
      previous_school: 'SD Bintang Kejora Jakarta',
      father_name: 'Hendrawan Wijaya',
      parent_phone: '0812-3456-7890',
      test_schedule: {
        slot_name: 'Sesi Tes & Observasi Masuk Batch 1',
        test_date: '2026-12-12',
        start_time: '09:00',
        end_time: '11:00',
        campus_location: 'Campus 1 (Pos Pengumben) - Jl. Pos Pengumben Lama No.13, Jakarta Barat',
        room_name: 'Ruang Asesmen Terpadu 102',
        assessment_title: lvl.assessment_title,
        assessment_subjects: lvl.assessment_subjects,
        assessment_preparation: lvl.assessment_preparation,
        assessment_rules: lvl.assessment_rules,
        parent_instructions: lvl.parent_instructions,
      }
    });
  };

  const isDirector = userRole === 'director';

  return (
    <div className="adm-fee-approvals-wrap">
      {/* 1. EXECUTIVE PAGE HEADER */}
      <div className="afa-page-header">
        <div className="afa-header-left">
          <div className="afa-role-pill">
            <ShieldCheck size={14} />
            <span>{categoryMeta.roleTitle}</span>
          </div>
          <h1 className="afa-header-title">{categoryMeta.title}</h1>
          <p className="afa-header-subtitle">{categoryMeta.subtitle}</p>
        </div>
        <div className="afa-header-right">
          <button
            type="button"
            className="afa-btn-refresh"
            onClick={loadData}
            disabled={loading}
            title="Segarkan data dari server"
          >
            <RefreshCw size={15} className={loading ? 'afa-spin' : ''} />
            <span>Segarkan Data</span>
          </button>
        </div>
      </div>

      {/* 2. SUMMARY KPI STAT CARDS */}
      <div className="afa-stats-row">
        <div className="afa-stat-card">
          <div className="afa-stat-icon-wrap" style={{ background: '#eff6ff', color: '#2563eb' }}>
            <categoryMeta.icon size={22} />
          </div>
          <div className="afa-stat-info">
            <span className="afa-stat-val">{totalItemCount}</span>
            <span className="afa-stat-label">Total {categoryMeta.itemName}</span>
          </div>
        </div>

        <div className="afa-stat-card highlight-amber">
          <div className="afa-stat-icon-wrap" style={{ background: '#fffbeb', color: '#d97706' }}>
            <Clock size={22} />
          </div>
          <div className="afa-stat-info">
            <span className="afa-stat-val" style={{ color: currentPendingCount > 0 ? '#b45309' : '#0f172a' }}>
              {currentPendingCount}
            </span>
            <span className="afa-stat-label">
              Menunggu Persetujuan {isDirector ? 'Direktur' : 'Principal'}
            </span>
          </div>
        </div>

        <div className="afa-stat-card highlight-green">
          <div className="afa-stat-icon-wrap" style={{ background: '#ecfdf5', color: '#059669' }}>
            <CheckCircle2 size={22} />
          </div>
          <div className="afa-stat-info">
            <span className="afa-stat-val" style={{ color: '#047857' }}>{approvedItemCount}</span>
            <span className="afa-stat-label">Telah Disetujui / Aktif</span>
          </div>
        </div>

        <div className="afa-stat-card">
          <div className="afa-stat-icon-wrap" style={{ background: '#faf5ff', color: '#7c3aed' }}>
            <ShieldCheck size={22} />
          </div>
          <div className="afa-stat-info">
            <span className="afa-stat-val" style={{ fontSize: '0.92rem', textTransform: 'uppercase' }}>
              {isDirector ? 'Direktur Sekolah' : 'Kepala Sekolah'}
            </span>
            <span className="afa-stat-label">Otoritas Tunggal Pengesahan</span>
          </div>
        </div>
      </div>

      {/* 3. FILTER & SEARCH BAR */}
      <div className="afa-filter-bar">
        <div className="afa-filter-group">
          <button
            type="button"
            className={`afa-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <span>Semua</span>
            <span className="afa-filter-badge">{totalItemCount}</span>
          </button>
          <button
            type="button"
            className={`afa-filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            <Clock size={13} />
            <span>Menunggu Approval</span>
            {currentPendingCount > 0 && (
              <span className="afa-filter-badge" style={{ background: '#d97706' }}>{currentPendingCount}</span>
            )}
          </button>
          <button
            type="button"
            className={`afa-filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => setStatusFilter('approved')}
          >
            <CheckCircle2 size={13} />
            <span>Telah Disetujui</span>
          </button>
        </div>

        <div className="afa-search-box">
          <Search size={15} className="afa-search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Cari ${categoryMeta.itemName.toLowerCase()}...`}
          />
          {searchQuery && (
            <button type="button" className="afa-clear-search" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* 4. FEEDBACK ALERT */}
      {feedback && (
        <div className={`afa-alert afa-alert-${feedback.type}`}>
          {feedback.type === 'success' && <CheckCircle2 size={18} />}
          {feedback.type === 'error' && <XCircle size={18} />}
          {feedback.type === 'info' && <AlertCircle size={18} />}
          <span>{feedback.text}</span>
          <button type="button" onClick={() => setFeedback(null)} className="afa-alert-close">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ==========================================================================
          5. TABLES SECTION (EXACT TABLE VIEW ACCORDING TO USER SPECIFICATION)
          ========================================================================== */}

      {/* TAB 1: STRUKTUR BIAYA JENJANG (LEVELS) */}
      {activeCategory === 'levels' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Jenjang Pendidikan</th>
                  <th>Status Pendaftaran</th>
                  <th>Status Approval Direktur Sekolah</th>
                  <th style={{ textAlign: 'right', minWidth: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredLevels.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="adm-empty-cell">
                      Tidak ada jenjang pendidikan yang sesuai dengan filter pencarian.
                    </td>
                  </tr>
                ) : (
                  filteredLevels.map((lvl) => {
                    const meta = LEVEL_META[lvl.level_code] || { title: lvl.level_name, tag: 'PH', color: '#64748b' };
                    const hasPending = lvl.fee_approval_status === 'pending_director' || lvl.fee_approval_status === 'pending_principal' || Boolean(lvl.pending_fee_payload);
                    const isApproved = lvl.fee_approval_status === 'approved';
                    const isRejected = lvl.fee_approval_status === 'rejected';

                    return (
                      <tr key={lvl.level_code} style={hasPending ? { background: '#fffbeb' } : {}}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="afa-level-tag" style={{ backgroundColor: meta.color }}>
                              {meta.tag}
                            </span>
                            <div>
                              <strong>{lvl.level_name}</strong>
                              <div className="adm-sub-text">
                                {meta.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {lvl.is_open ? (
                            <span className="adm-status-pill adm-badge-green">Dibuka Resmi</span>
                          ) : (
                            <span className="adm-status-pill adm-badge-orange">
                              {lvl.level_code === 'sma' ? 'Coming Soon' : 'Ditutup Sementara'}
                            </span>
                          )}
                          {lvl.coming_soon_note && <div className="adm-sub-text">{lvl.coming_soon_note}</div>}
                        </td>
                        <td>
                          {hasPending && (
                            <span className="adm-status-pill adm-badge-orange" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Menunggu Persetujuan Direktur</span>
                            </span>
                          )}
                          {isApproved && (
                            <span className="adm-status-pill adm-badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle2 size={12} />
                              <span>Disetujui Direktur Sekolah</span>
                            </span>
                          )}
                          {isRejected && (
                            <span className="adm-status-pill adm-badge-red" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <XCircle size={12} />
                              <span>Ditolak</span>
                            </span>
                          )}
                          {!hasPending && !isApproved && !isRejected && (
                            <span className="adm-status-pill adm-badge-gray">Draft</span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => handleOpenDetail(lvl, 'level')}
                              title="Lihat rincian lengkap tarif biaya & kelola persetujuan"
                            >
                              <Eye size={13} />
                              <span>Lihat Detail</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: GELOMBANG PERIODE PENDAFTARAN & KUOTA (PERIODS) */}
      {activeCategory === 'periods' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Nama Gelombang / Periode</th>
                  <th>Tahun Ajaran</th>
                  <th>Rentang Tanggal</th>
                  <th>Alokasi Kuota Kursi</th>
                  <th>Status Gelombang</th>
                  <th style={{ textAlign: 'right', minWidth: 200 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredPeriods.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="adm-empty-cell">
                      Tidak ada periode pendaftaran yang sesuai dengan filter pencarian.
                    </td>
                  </tr>
                ) : (
                  filteredPeriods.map((p) => {
                    const isPending = Boolean(p.is_pending_approval);
                    const isCreate = p.action_type === 'create';
                    const isUpdate = p.action_type === 'update';
                    const isDelete = p.action_type === 'delete';
                    const todayStr = new Date().toISOString().split('T')[0];
                    const isPastEndDate = Boolean(p.end_date && p.end_date < todayStr);

                    return (
                      <tr key={p.id} style={isPending ? { background: '#fffbeb' } : {}}>
                        <td>
                          <strong>{p.period_name}</strong>
                          {isPending && (
                            <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700, marginTop: 3 }}>
                              {isCreate && 'Pengajuan gelombang baru menanti persetujuan Direktur Sekolah.'}
                              {isUpdate && 'Usulan revisi periode & kuota menanti persetujuan Direktur Sekolah.'}
                              {isDelete && 'Usulan penghapusan periode menanti persetujuan Direktur Sekolah.'}
                            </div>
                          )}
                          {p.notes && <div className="adm-sub-text">{p.notes}</div>}
                        </td>
                        <td>
                          <span className="adm-mono">{p.academic_year || '2026/2027'}</span>
                        </td>
                        <td>
                          {formatDateId(p.start_date)} s/d {formatDateId(p.end_date)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11.5, background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                              PS: {p.quotas?.preschool ?? 0}
                            </span>
                            <span style={{ fontSize: 11.5, background: '#fef3c7', color: '#b45309', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                              KG: {p.quotas?.kindergarten ?? 0}
                            </span>
                            <span style={{ fontSize: 11.5, background: '#f3e8ff', color: '#6b21a8', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                              PRI: {p.quotas?.primary ?? 0}
                            </span>
                            <span style={{ fontSize: 11.5, background: '#ecfdf5', color: '#047857', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                              SEC: {p.quotas?.secondary ?? 0}
                            </span>
                          </div>
                        </td>
                        <td>
                          {isPending ? (
                            <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Menunggu Persetujuan Direktur</span>
                            </span>
                          ) : isPastEndDate ? (
                            <span className="adm-status-pill adm-badge-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Ditutup (Berakhir)</span>
                            </span>
                          ) : p.is_active ? (
                            <span className="adm-status-pill adm-badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle2 size={12} />
                              <span>Aktif &amp; Buka</span>
                            </span>
                          ) : (
                            <span className="adm-status-pill adm-badge-gray">Ditutup</span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => handleOpenDetail(p, 'period')}
                              title="Lihat detail periode pendaftaran"
                            >
                              <Eye size={13} />
                              <span>Lihat Detail</span>
                            </button>

                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  className="adm-btn-action-approve"
                                  disabled={actionLoading}
                                  onClick={() => handleDirectApprove(p, 'periods')}
                                  title="Setujui pengajuan periode ini"
                                >
                                  <Check size={13} />
                                  <span>Setujui</span>
                                </button>
                                <button
                                  type="button"
                                  className="adm-btn-action-reject"
                                  disabled={actionLoading}
                                  onClick={() => handleOpenRejectModal(p, 'periods')}
                                  title="Tolak pengajuan periode ini"
                                >
                                  <X size={13} />
                                  <span>Tolak</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 3: KODE PROMO & VOUCHER (PROMO CODES) */}
      {activeCategory === 'promo_codes' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Kode &amp; Program Promo</th>
                  <th>Besaran Potongan</th>
                  <th>Berlaku Jenjang</th>
                  <th>Masa Berlaku</th>
                  <th>Status &amp; Kuota</th>
                  <th style={{ textAlign: 'right', minWidth: 200 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredPromos.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="adm-empty-cell">
                      Tidak ada kode promo yang sesuai dengan filter pencarian.
                    </td>
                  </tr>
                ) : (
                  filteredPromos.map((pr) => {
                    const isPending = Boolean(pr.is_pending_approval);
                    return (
                      <tr key={pr.id} style={isPending ? { background: '#fffbeb' } : {}}>
                        <td>
                          <strong className="adm-mono" style={{ color: '#2563eb' }}>{pr.promo_code}</strong>
                          <div style={{ fontWeight: 600, color: '#1e293b', marginTop: 2 }}>{pr.promo_title}</div>
                          {isPending && (
                            <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                              Pengajuan voucher menanti persetujuan Direktur Sekolah.
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="adm-mono" style={{ fontWeight: 700, color: '#059669' }}>
                            {pr.discount_type === 'percentage'
                              ? `${pr.discount_value}% dari DF`
                              : formatRupiah(pr.discount_value)}
                          </span>
                        </td>
                        <td>
                          <span className="adm-mono">
                            {pr.applies_to_levels === 'all' ? 'Semua Jenjang' : pr.applies_to_levels.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          {formatDateId(pr.valid_until)}
                        </td>
                        <td>
                          {isPending ? (
                            <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Menunggu Persetujuan Direktur</span>
                            </span>
                          ) : (
                            <span className="adm-status-pill adm-badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle2 size={12} />
                              <span>Disetujui &amp; Kuota {pr.max_quota || 100}</span>
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => handleOpenDetail(pr, 'promo')}
                              title="Lihat detail voucher promo"
                            >
                              <Eye size={13} />
                              <span>Lihat Detail</span>
                            </button>

                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  className="adm-btn-action-approve"
                                  disabled={actionLoading}
                                  onClick={() => handleDirectApprove(pr, 'promo_codes')}
                                  title="Setujui voucher promo ini"
                                >
                                  <Check size={13} />
                                  <span>Setujui</span>
                                </button>
                                <button
                                  type="button"
                                  className="adm-btn-action-reject"
                                  disabled={actionLoading}
                                  onClick={() => handleOpenRejectModal(pr, 'promo_codes')}
                                  title="Tolak voucher promo ini"
                                >
                                  <X size={13} />
                                  <span>Tolak</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: JADWAL SESI TES OFFLINE (TEST SLOTS - PRINCIPAL) */}
      {activeCategory === 'test_slots' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Nama Sesi Tes &amp; Ruangan</th>
                  <th>Jenjang Target</th>
                  <th>Jadwal Pelaksanaan</th>
                  <th>Status &amp; Kapasitas</th>
                  <th style={{ textAlign: 'right', minWidth: 200 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredSlots.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="adm-empty-cell">
                      Tidak ada jadwal sesi tes yang sesuai dengan filter pencarian.
                    </td>
                  </tr>
                ) : (
                  filteredSlots.map((sl) => {
                    const isPending = Boolean(sl.is_pending_approval);
                    return (
                      <tr key={sl.id} style={isPending ? { background: '#fffbeb' } : {}}>
                        <td>
                          <strong>{sl.session_name}</strong>
                          {isPending && (
                            <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                              Pengajuan sesi menanti persetujuan Kepala Sekolah (Principal).
                            </div>
                          )}
                          <div className="adm-sub-text">
                            {sl.campus_location?.split(' - ')[0]} &bull; {sl.room_name || '-'}
                          </div>
                          {sl.custom_notes && <div className="adm-sub-text" style={{ fontStyle: 'italic' }}>{sl.custom_notes}</div>}
                        </td>
                        <td>
                          <span className="adm-mono">
                            {sl.level_code === 'all' ? 'Semua Jenjang' : sl.level_code.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <div>
                            <strong>{formatDateId(sl.test_date)}</strong>
                            <div className="adm-sub-text">{sl.test_time}</div>
                          </div>
                        </td>
                        <td>
                          {isPending ? (
                            <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Menunggu Persetujuan Principal</span>
                            </span>
                          ) : (
                            <span className="adm-status-pill adm-badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle2 size={12} />
                              <span>Disetujui &bull; Kuota: {sl.slot_quota || 15} Kursi</span>
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => handleOpenDetail(sl, 'slot')}
                              title="Lihat detail sesi tes offline"
                            >
                              <Eye size={13} />
                              <span>Lihat Detail</span>
                            </button>

                            {isPending && (
                              <>
                                <button
                                  type="button"
                                  className="adm-btn-action-approve"
                                  disabled={actionLoading}
                                  onClick={() => handleDirectApprove(sl, 'test_slots')}
                                  title="Setujui sesi tes ini"
                                >
                                  <Check size={13} />
                                  <span>Setujui</span>
                                </button>
                                <button
                                  type="button"
                                  className="adm-btn-action-reject"
                                  disabled={actionLoading}
                                  onClick={() => handleOpenRejectModal(sl, 'test_slots')}
                                  title="Tolak sesi tes ini"
                                >
                                  <X size={13} />
                                  <span>Tolak</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 5: MATERI ASESMEN & KARTU TES (ASSESSMENT CURRICULUM - PRINCIPAL) */}
      {activeCategory === 'assessment' && (
        <div className="adm-table-card">
          <div className="adm-data-table-wrap">
            <table className="adm-data-table">
              <thead>
                <tr>
                  <th>Jenjang Pendidikan</th>
                  <th>Judul Asesmen Resmi</th>
                  <th>Status Approval</th>
                  <th style={{ textAlign: 'right', minWidth: 140 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="adm-empty-cell">
                      Tidak ada materi asesmen yang sesuai dengan filter pencarian.
                    </td>
                  </tr>
                ) : (
                  filteredAssessments.map((lvl) => {
                    const meta = LEVEL_META[lvl.level_code] || { title: lvl.level_name, tag: 'PH', color: '#64748b' };
                    const isPending = Boolean(lvl.is_pending_approval);

                    return (
                      <tr key={lvl.level_code} style={isPending ? { background: '#fffbeb' } : {}}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="afa-level-tag" style={{ backgroundColor: meta.color }}>
                              {meta.tag}
                            </span>
                            <div>
                              <strong>{lvl.level_name}</strong>
                              <div className="adm-sub-text">{meta.title}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong>{lvl.assessment_title || '-'}</strong>
                          {isPending && (
                            <div style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginTop: 3 }}>
                              Perubahan materi seleksi menanti persetujuan Kepala Sekolah (Principal).
                            </div>
                          )}
                        </td>
                        <td>
                          {isPending ? (
                            <span className="adm-status-pill adm-badge-amber" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Clock size={12} />
                              <span>Menunggu Persetujuan Principal</span>
                            </span>
                          ) : (
                            <span className="adm-status-pill adm-badge-green" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <CheckCircle2 size={12} />
                              <span>Disetujui Principal &amp; Aktif</span>
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="adm-cell-actions" style={{ justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="adm-btn-action-view"
                              onClick={() => handleOpenDetail(lvl, 'assessment')}
                              title="Lihat detail lengkap kurikulum, kartu tes & kelola persetujuan"
                            >
                              <Eye size={13} />
                              <span>Lihat Detail</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==========================================================================
          6. READ-ONLY DETAIL MODAL (MODAL READ-ONLY SESUAI PERMINTAAN USER)
          ========================================================================== */}
      {detailModalItem && (
        <div className="adm-config-modal-overlay" onClick={() => setDetailModalItem(null)}>
          <div className="adm-config-modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 880 }}>
            {/* MODAL HEADER */}
            <div className="adm-config-modal-head">
              <div className="adm-config-modal-head-title">
                <div className="adm-config-modal-head-icon" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
                  <Eye size={20} />
                </div>
                <div>
                  <h3>
                    {detailModalType === 'level' && `Detail Struktur Biaya: ${detailModalItem.level_name}`}
                    {detailModalType === 'period' && `Detail Periode Pendaftaran: ${detailModalItem.period_name || detailModalItem.target_label}`}
                    {detailModalType === 'promo' && `Detail Voucher Promo: ${detailModalItem.promo_code}`}
                    {detailModalType === 'slot' && `Detail Sesi Tes Masuk: ${detailModalItem.session_name}`}
                    {detailModalType === 'assessment' && `Detail Materi Seleksi: ${detailModalItem.level_name}`}
                  </h3>
                  <span className="adm-config-modal-head-tag">
                    Mode Pratinjau Otorisasi (Hanya Lihat • Verifikasi Persetujuan {isDirector ? 'Direktur Sekolah' : 'Kepala Sekolah'})
                  </span>
                </div>
              </div>
              <button type="button" className="adm-config-modal-close" onClick={() => setDetailModalItem(null)}>
                <X size={18} />
              </button>
            </div>

            {/* SUBNAV FOR LEVEL */}
            {detailModalType === 'level' && (
              <div className="adm-modal-subnav">
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'fees' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('fees')}
                >
                  <CreditCard size={15} />
                  <span>1. Rincian Tarif Biaya &amp; Uang Pangkal</span>
                </button>
                <button
                  type="button"
                  className={`adm-modal-subnav-btn ${feeModalTab === 'bank_sign' ? 'active' : ''}`}
                  onClick={() => setFeeModalTab('bank_sign')}
                >
                  <ShieldCheck size={15} />
                  <span>2. Rekening &amp; Pejabat Penandatangan</span>
                </button>
              </div>
            )}

            {/* MODAL BODY */}
            <div className="adm-config-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px 24px' }}>
              {/* MODAL CONTENT: LEVEL (FEE STRUCTURES) */}
              {detailModalType === 'level' && (
                <>
                  {detailModalItem.fee_approval_status === 'approved' && (
                    <div style={{ background: '#ecfdf5', border: '1.5px solid #a7f3d0', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#065f46', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <CheckCircle2 size={18} color="#059669" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Status Tarif Biaya: Telah Disetujui Direktur Sekolah (School Director) &amp; Resmi Aktif</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#047857' }}>
                          Tarif biaya ini telah diberlakukan secara resmi pada invoice dan formulir pendaftaran orang tua siswa.
                        </p>
                      </div>
                    </div>
                  )}

                  {(detailModalItem.fee_approval_status === 'pending_director' || Boolean(detailModalItem.pending_fee_payload)) && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Status: Menunggu Persetujuan Direktur Sekolah (School Director)</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                          Admin Admissions telah mengajukan usulan penyesuaian nominal tarif biaya baru. Silakan periksa perbandingan tarif di bawah ini sebelum menyetujui.
                        </p>
                      </div>
                    </div>
                  )}

                  {feeModalTab === 'fees' && (
                    <>
                      {/* INFORMASI PENGAJUAN & STATUS PENDAFTARAN */}
                      <div className="adm-ro-grid-3" style={{ marginBottom: 16 }}>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Status Pendaftaran Jenjang</span>
                          <span className="adm-ro-val" style={{ fontWeight: 700, color: detailModalItem.is_open ? '#047857' : '#b45309' }}>
                            {detailModalItem.is_open ? 'Dibuka Resmi (Menerima Pendaftaran)' : (detailModalItem.level_code === 'sma' ? 'Coming Soon' : 'Ditutup Sementara')}
                          </span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Diajukan / Diperbarui Oleh</span>
                          <span className="adm-ro-val" style={{ fontWeight: 600 }}>
                            {detailModalItem.fee_updated_by_name || 'Admin Admissions'}
                          </span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Waktu Pengajuan / Perubahan</span>
                          <span className="adm-ro-val">
                            {formatDateDisplay(detailModalItem.fee_updated_at || detailModalItem.updated_at)}
                          </span>
                        </div>
                      </div>

                      {/* KOMPARASI JIKA ADA PENDING */}
                      {Boolean(detailModalItem.pending_fee_payload) && (() => {
                        const proposed = typeof detailModalItem.pending_fee_payload === 'string'
                          ? JSON.parse(detailModalItem.pending_fee_payload || '{}')
                          : (detailModalItem.pending_fee_payload || {});

                        const feeFields = [
                          { key: 'registration_fee', label: 'Biaya Pendaftaran Formulir (Registration Fee)' },
                          { key: 'test_reg_form_fee', label: 'Biaya Formulir Tes Seleksi Masuk' },
                          { key: 'admission_fee', label: 'Uang Masuk / Matrikulasi (Admission Fee)' },
                          { key: 'development_fee', label: 'Uang Pangkal Total (Development Fee)' },
                          { key: 'df_1_amount', label: 'Termin DF 1 (Saat Daftar Ulang)' },
                          { key: 'df_2_amount', label: 'Termin DF 2 (Bulan ke-2)' },
                          { key: 'df_3_amount', label: 'Termin DF 3 (Bulan ke-3)' },
                          { key: 'df_4_amount', label: 'Termin DF 4 (Bulan ke-4)' },
                          { key: 'tuition_fee', label: 'Biaya SPP Bulanan (Tuition Fee)' },
                          { key: 'books_activity_fee', label: 'Biaya Buku & Kegiatan Tahunan' },
                        ];

                        return (
                          <div style={{ marginBottom: 20 }}>
                            <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <AlertCircle size={15} color="#d97706" />
                              <span>Tabel Komparasi: Tarif Resmi Saat Ini vs Usulan Perubahan Baru</span>
                            </h4>
                            <div className="adm-diff-table-wrap">
                              <table className="adm-diff-table">
                                <thead>
                                  <tr>
                                    <th>Komponen Biaya</th>
                                    <th>Tarif Resmi Saat Ini</th>
                                    <th>Usulan Tarif Baru</th>
                                    <th>Status Perubahan</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {feeFields.map(f => {
                                    const currentVal = Number(detailModalItem[f.key] || 0);
                                    const proposedVal = Number(proposed[f.key] !== undefined ? proposed[f.key] : currentVal);
                                    const isDiff = Math.abs(currentVal - proposedVal) > 0.01;

                                    return (
                                      <tr key={f.key} style={isDiff ? { background: '#fffbeb' } : {}}>
                                        <td><strong>{f.label}</strong></td>
                                        <td>{formatRupiah(currentVal)}</td>
                                        <td style={isDiff ? { color: '#b45309', fontWeight: 700 } : {}}>
                                          {formatRupiah(proposedVal)}
                                        </td>
                                        <td>
                                          {isDiff ? (
                                            <span className="adm-status-pill adm-badge-orange" style={{ fontSize: '0.72rem' }}>
                                              Berubah ({formatRupiah(proposedVal - currentVal)})
                                            </span>
                                          ) : (
                                            <span style={{ fontSize: '0.74rem', color: '#64748b' }}>Tetap / Tidak Berubah</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}

                      {/* RINCIAN TARIF BIAYA READ-ONLY */}
                      <div className="adm-ro-grid-3">
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Biaya Formulir Registrasi</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.registration_fee)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Biaya Tes Seleksi Masuk</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.test_reg_form_fee)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Uang Masuk / Matrikulasi</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.admission_fee)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Uang Pangkal Total (DF)</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.development_fee)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Termin DF 1 (Daftar Ulang)</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.df_1_amount)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Termin DF 2 (Bulan ke-2)</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.df_2_amount)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Termin DF 3 (Bulan ke-3)</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.df_3_amount)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">Termin DF 4 (Bulan ke-4)</span>
                          <span className="adm-ro-val">{formatRupiah(detailModalItem.df_4_amount)}</span>
                        </div>
                        <div className="adm-ro-field">
                          <span className="adm-ro-label">SPP Bulanan (Tuition Fee)</span>
                          <span className="adm-ro-val" style={{ color: '#0369a1', fontWeight: 800 }}>
                            {formatRupiah(detailModalItem.tuition_fee)}
                          </span>
                        </div>
                      </div>

                      {detailModalItem.df_due_notes && (
                        <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.82rem', color: '#334155' }}>
                          <strong>Catatan Khusus Termin Uang Pangkal:</strong> {detailModalItem.df_due_notes}
                        </div>
                      )}
                    </>
                  )}

                  {feeModalTab === 'bank_sign' && (
                    <div className="adm-ro-grid-2">
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Nama Bank Resmi</span>
                        <span className="adm-ro-val">{detailModalItem.bank_name || 'BCA'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Nomor Rekening Resmi Sekolah</span>
                        <span className="adm-ro-val adm-mono" style={{ fontSize: '0.95rem', color: '#1e40af' }}>
                          {detailModalItem.bank_account_number || '-'}
                        </span>
                      </div>
                      <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                        <span className="adm-ro-label">Nama Pemilik Rekening (Atas Nama)</span>
                        <span className="adm-ro-val">{detailModalItem.bank_account_holder || '-'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Nama Pimpinan Penandatangan LoA</span>
                        <span className="adm-ro-val">{detailModalItem.principal_name || 'Dr. Amelia Hartanto, M.Ed.'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Jabatan Resmi Penandatangan</span>
                        <span className="adm-ro-val">{detailModalItem.principal_title || 'School Director'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Nama PIC Keuangan &amp; Kasir</span>
                        <span className="adm-ro-val">{detailModalItem.finance_pic_name || 'Zahra, S.E.'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Jabatan PIC Keuangan</span>
                        <span className="adm-ro-val">{detailModalItem.finance_pic_title || 'Finance & Treasury PIC'}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* MODAL CONTENT: PERIOD */}
              {detailModalType === 'period' && (() => {
                const todayStr = new Date().toISOString().split('T')[0];
                const isPastEnd = Boolean(detailModalItem.end_date && detailModalItem.end_date < todayStr);
                const periodApps = (applications || []).filter(
                  (app) => String(app.period_id) === String(detailModalItem.id)
                );
                const filledCounts = {
                  preschool: periodApps.filter(a => {
                    const l = (a.level_code || '').toLowerCase();
                    return l.includes('preschool') || l === 'ps' || l === 'toddler';
                  }).length,
                  kindergarten: periodApps.filter(a => {
                    const l = (a.level_code || '').toLowerCase();
                    return l.includes('kindergarten') || l === 'kg' || l === 'tk';
                  }).length,
                  primary: periodApps.filter(a => {
                    const l = (a.level_code || '').toLowerCase();
                    return l.includes('primary') || l === 'sd' || l === 'pri';
                  }).length,
                  secondary: periodApps.filter(a => {
                    const l = (a.level_code || '').toLowerCase();
                    return l.includes('secondary') || l === 'smp' || l === 'sec';
                  }).length,
                };

                const qPs = Number(detailModalItem.quotas?.preschool ?? 0);
                const qKg = Number(detailModalItem.quotas?.kindergarten ?? 0);
                const qPri = Number(detailModalItem.quotas?.primary ?? 0);
                const qSec = Number(detailModalItem.quotas?.secondary ?? 0);

                return (
                  <div>
                    {detailModalItem.is_pending_approval ? (
                      <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Clock size={18} color="#d97706" style={{ flexShrink: 0 }} />
                        <div>
                          <strong>Menunggu Persetujuan Direktur Sekolah</strong>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                            Diajukan oleh: <strong>{detailModalItem.requested_by_name || 'Admin Admissions'}</strong> &bull; Waktu: {formatDateDisplay(detailModalItem.created_at)}
                          </p>
                        </div>
                      </div>
                    ) : isPastEnd ? (
                      <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#475569', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Clock size={18} color="#64748b" style={{ flexShrink: 0 }} />
                        <div>
                          <strong>Status: Periode Ditutup (Telah Berakhir)</strong>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                            Batas waktu pendaftaran pada gelombang ini telah lewat ({formatDateId(detailModalItem.end_date)}). Status otomatis ditutup.
                          </p>
                        </div>
                      </div>
                    ) : detailModalItem.is_active ? (
                      <div style={{ background: '#ecfdf5', border: '1.5px solid #a7f3d0', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#065f46', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <CheckCircle2 size={18} color="#059669" style={{ flexShrink: 0 }} />
                        <div>
                          <strong>Status: Gelombang Pendaftaran Aktif &amp; Terbuka</strong>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#047857' }}>
                            Periode pendaftaran resmi ini sedang aktif menerima pendaftaran orang tua siswa.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#475569', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Info size={18} color="#64748b" style={{ flexShrink: 0 }} />
                        <div>
                          <strong>Status: Periode Nonaktif / Ditutup</strong>
                        </div>
                      </div>
                    )}

                    <div className="adm-ro-grid-2">
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Nama Gelombang / Periode</span>
                        <span className="adm-ro-val">{detailModalItem.period_name}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Tahun Ajaran</span>
                        <span className="adm-ro-val adm-mono">{detailModalItem.academic_year || '2026/2027'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Tanggal Mulai Buka</span>
                        <span className="adm-ro-val">{formatDateId(detailModalItem.start_date)}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Tanggal Terakhir Ditutup</span>
                        <span className="adm-ro-val">{formatDateId(detailModalItem.end_date)}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Diajukan / Diperbarui Oleh</span>
                        <span className="adm-ro-val" style={{ fontWeight: 600 }}>{detailModalItem.requested_by_name || 'Admin Admissions'}</span>
                      </div>
                      <div className="adm-ro-field">
                        <span className="adm-ro-label">Waktu Pengajuan / Pembaruan</span>
                        <span className="adm-ro-val">{formatDateDisplay(detailModalItem.created_at || detailModalItem.updated_at)}</span>
                      </div>
                      {detailModalItem.notes && (
                        <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                          <span className="adm-ro-label">Catatan Admin Admissions</span>
                          <span className="adm-ro-val">{detailModalItem.notes}</span>
                        </div>
                      )}
                    </div>

                    <h4 style={{ margin: '20px 0 10px', fontSize: '0.88rem', color: '#0f172a' }}>
                      Alokasi Kuota Kursi &amp; Sisa Kuota Tiap Jenjang
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: '#0369a1', fontWeight: 700 }}>PRESCHOOL (KB)</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Kuota: {qPs}</span>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Terisi: {filledCounts.preschool}</span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0369a1', marginTop: 4 }}>
                          Sisa: {Math.max(0, qPs - filledCounts.preschool)} <span style={{ fontSize: 11, fontWeight: 500 }}>Kursi</span>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700 }}>KINDERGARTEN (TK)</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Kuota: {qKg}</span>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Terisi: {filledCounts.kindergarten}</span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#b45309', marginTop: 4 }}>
                          Sisa: {Math.max(0, qKg - filledCounts.kindergarten)} <span style={{ fontSize: 11, fontWeight: 500 }}>Kursi</span>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: '#6b21a8', fontWeight: 700 }}>PRIMARY (SD)</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Kuota: {qPri}</span>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Terisi: {filledCounts.primary}</span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#6b21a8', marginTop: 4 }}>
                          Sisa: {Math.max(0, qPri - filledCounts.primary)} <span style={{ fontSize: 11, fontWeight: 500 }}>Kursi</span>
                        </div>
                      </div>

                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: '#047857', fontWeight: 700 }}>SECONDARY (SMP)</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Kuota: {qSec}</span>
                          <span style={{ fontSize: 11.5, color: '#64748b' }}>Terisi: {filledCounts.secondary}</span>
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#047857', marginTop: 4 }}>
                          Sisa: {Math.max(0, qSec - filledCounts.secondary)} <span style={{ fontSize: 11, fontWeight: 500 }}>Kursi</span>
                        </div>
                      </div>
                    </div>

                    {/* SUB-TABLE: CALON SISWA YANG DAFTAR KE PERIODE INI */}
                    <div style={{ marginTop: 22 }}>
                      <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', color: '#0f172a' }}>
                        Daftar Calon Siswa Terdaftar pada Gelombang Ini ({periodApps.length})
                      </h4>
                      {periodApps.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px 16px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#64748b' }}>
                          <Users size={28} color="#94a3b8" style={{ margin: '0 auto 6px', display: 'block' }} />
                          <strong style={{ display: 'block', fontSize: 13, color: '#1e293b' }}>Belum Ada Pendaftar Masuk</strong>
                          <p style={{ margin: '3px 0 0', fontSize: 12 }}>
                            Calon siswa yang mendaftar online pada gelombang ini akan otomatis tampil di sini.
                          </p>
                        </div>
                      ) : (
                        <div className="adm-data-table-wrap">
                          <table className="adm-data-table">
                            <thead>
                              <tr>
                                <th>No. Registrasi</th>
                                <th>Nama Calon Siswa</th>
                                <th>Jenjang</th>
                                <th>Orang Tua &amp; Kontak</th>
                                <th>Status Pendaftaran</th>
                              </tr>
                            </thead>
                            <tbody>
                              {periodApps.map((att) => {
                                const regNumber = att.registration_no || att.registration_number || (att.id ? `REG-2026-${String(att.id).padStart(4, '0')}` : '-');
                                const pName = att.parent_name || att.applicant_name || att.parent_info?.full_name || att.father_name || att.mother_name || att.form_data?.parent_name || '-';
                                const pPhone = att.parent_phone || att.applicant_phone || att.parent_info?.phone || att.whatsapp_number || att.form_data?.phone || '-';
                                const stCode = (att.status_code || '').toLowerCase();

                                let badgeStyle = { fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600, display: 'inline-flex', alignItems: 'center' };
                                let badgeLabel = att.status_label || stCode;

                                if (stCode === 'enrolled' || stCode === 'admitted') {
                                  badgeStyle = { ...badgeStyle, background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' };
                                  badgeLabel = 'Siswa Resmi Terdaftar';
                                } else if (stCode === 'test_passed' || stCode === 'passed') {
                                  badgeStyle = { ...badgeStyle, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
                                  badgeLabel = 'Lolos Seleksi & LoA Terbit';
                                } else if (stCode === 'test_scheduled' || stCode === 'scheduled') {
                                  badgeStyle = { ...badgeStyle, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' };
                                  badgeLabel = 'Jadwal Tes Ditetapkan';
                                } else if (stCode === 'test_failed' || stCode === 'failed') {
                                  badgeStyle = { ...badgeStyle, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fca5a5' };
                                  badgeLabel = 'Tidak Lolos Seleksi';
                                } else if (stCode === 'document_verified' || stCode === 'verified') {
                                  badgeStyle = { ...badgeStyle, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' };
                                  badgeLabel = 'Berkas Terverifikasi';
                                } else if (stCode === 'payment_pending' || stCode === 'waiting_payment') {
                                  badgeStyle = { ...badgeStyle, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
                                  badgeLabel = 'Menunggu Pembayaran';
                                } else {
                                  badgeStyle = { ...badgeStyle, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
                                  badgeLabel = 'Menunggu Verifikasi Berkas';
                                }

                                return (
                                  <tr key={att.id}>
                                    <td><strong className="adm-mono">{regNumber}</strong></td>
                                    <td><strong>{att.child_full_name}</strong></td>
                                    <td><span className="adm-mono">{(att.level_name || att.level_code || '').toUpperCase()}</span></td>
                                    <td>
                                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{pName}</div>
                                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{pPhone}</div>
                                    </td>
                                    <td>
                                      <span style={badgeStyle}>
                                        {badgeLabel}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* MODAL CONTENT: PROMO */}
              {detailModalType === 'promo' && (
                <div>
                  {detailModalItem.is_pending_approval && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Menunggu Persetujuan Direktur Sekolah</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                          Diajukan oleh: <strong>{detailModalItem.requested_by_name || 'Admin Admissions'}</strong> &bull; Waktu: {formatDateDisplay(detailModalItem.created_at)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="adm-ro-grid-2">
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Kode Voucher Promo</span>
                      <span className="adm-ro-val adm-mono" style={{ fontSize: '1.05rem', color: '#2563eb' }}>
                        {detailModalItem.promo_code}
                      </span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Nama Program Promo</span>
                      <span className="adm-ro-val">{detailModalItem.promo_title}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Besaran Potongan / Diskon</span>
                      <span className="adm-ro-val" style={{ color: '#059669', fontWeight: 800 }}>
                        {detailModalItem.discount_type === 'percentage'
                          ? `${detailModalItem.discount_value}% dari DF`
                          : formatRupiah(detailModalItem.discount_value)}
                      </span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Jenjang Pendidikan yang Berlaku</span>
                      <span className="adm-ro-val">
                        {detailModalItem.applies_to_levels === 'all' ? 'Semua Jenjang' : detailModalItem.applies_to_levels.toUpperCase()}
                      </span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Batas Kuota Pemakaian</span>
                      <span className="adm-ro-val">{detailModalItem.max_quota || 100} Penggunaan</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Masa Berlaku Kupon</span>
                      <span className="adm-ro-val">{formatDateId(detailModalItem.valid_until)}</span>
                    </div>
                  </div>

                  {/* SUB-TABLE: SISWA PENGGUNA VOUCHER PROMO */}
                  {(() => {
                    const promoUsers = (applications || []).filter(a => {
                      const c = (detailModalItem.promo_code || '').toUpperCase();
                      const uCode = (a.promo_code_used || '').toUpperCase();
                      const invCode = (a.invoice?.promo_code || '').toUpperCase();
                      return c && (uCode === c || invCode === c);
                    });

                    return (
                      <div style={{ marginTop: 22 }}>
                        <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', color: '#0f172a' }}>
                          Daftar Calon Siswa Pengguna Voucher ({promoUsers.length})
                        </h4>
                        {promoUsers.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px 16px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#64748b' }}>
                            <Ticket size={24} color="#94a3b8" style={{ margin: '0 auto 6px', display: 'block' }} />
                            <span style={{ fontSize: 12.5 }}>Belum ada calon siswa yang menggunakan kode voucher promo ini.</span>
                          </div>
                        ) : (
                          <div className="adm-data-table-wrap">
                            <table className="adm-data-table">
                              <thead>
                                <tr>
                                  <th>No. Registrasi</th>
                                  <th>Nama Calon Siswa</th>
                                  <th>Jenjang</th>
                                  <th>Orang Tua &amp; Kontak</th>
                                  <th>Potongan Diskon</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {promoUsers.map((u) => {
                                  const regNumber = u.registration_no || u.registration_number || (u.id ? `REG-2026-${String(u.id).padStart(4, '0')}` : '-');
                                  const pName = u.parent_name || u.applicant_name || u.parent_info?.full_name || '-';
                                  const pPhone = u.parent_phone || u.applicant_phone || u.parent_info?.phone || '-';
                                  const stCode = (u.status_code || '').toLowerCase();

                                  let badgeStyle = { fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600, display: 'inline-flex', alignItems: 'center' };
                                  let badgeLabel = u.status_label || stCode;

                                  if (stCode === 'enrolled' || stCode === 'admitted') {
                                    badgeStyle = { ...badgeStyle, background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' };
                                    badgeLabel = 'Siswa Resmi';
                                  } else if (stCode === 'test_passed' || stCode === 'passed') {
                                    badgeStyle = { ...badgeStyle, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' };
                                    badgeLabel = 'Lolos Seleksi';
                                  } else {
                                    badgeStyle = { ...badgeStyle, background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' };
                                    badgeLabel = 'Menunggu';
                                  }

                                  return (
                                    <tr key={u.id}>
                                      <td><strong className="adm-mono">{regNumber}</strong></td>
                                      <td><strong>{u.child_full_name}</strong></td>
                                      <td><span className="adm-mono">{(u.level_name || u.level_code || '').toUpperCase()}</span></td>
                                      <td>
                                        <div style={{ fontWeight: 600 }}>{pName}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{pPhone}</div>
                                      </td>
                                      <td><strong className="adm-mono" style={{ color: '#059669' }}>{formatRupiah(u.discount_amount || 0)}</strong></td>
                                      <td><span style={badgeStyle}>{badgeLabel}</span></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* MODAL CONTENT: TEST SLOT */}
              {detailModalType === 'slot' && (
                <div>
                  {detailModalItem.is_pending_approval && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Menunggu Persetujuan Kepala Sekolah (Principal)</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                          Diajukan oleh: <strong>{detailModalItem.requested_by_name || 'Admin Admissions'}</strong> &bull; Waktu: {formatDateDisplay(detailModalItem.created_at)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="adm-ro-grid-2">
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Nama Sesi Tes Offline</span>
                      <span className="adm-ro-val">{detailModalItem.session_name}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Jenjang Target</span>
                      <span className="adm-ro-val">
                        {detailModalItem.level_code === 'all' ? 'Semua Jenjang' : detailModalItem.level_code.toUpperCase()}
                      </span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Tanggal Pelaksanaan</span>
                      <span className="adm-ro-val">{formatDateId(detailModalItem.test_date)}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Waktu Sesi Tes</span>
                      <span className="adm-ro-val">{detailModalItem.test_time}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Lokasi Kampus</span>
                      <span className="adm-ro-val">{detailModalItem.campus_location}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Ruangan Tes</span>
                      <span className="adm-ro-val">{detailModalItem.room_name || '-'}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Alokasi Kapasitas Kursi</span>
                      <span className="adm-ro-val">{detailModalItem.slot_quota || 15} Siswa</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Jumlah Terisi Saat Ini</span>
                      <span className="adm-ro-val">{detailModalItem.booked_count || 0} Siswa</span>
                    </div>
                  </div>

                  {detailModalItem.custom_notes && (
                    <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.82rem', color: '#334155' }}>
                      <strong>Catatan Khusus Sesi:</strong> {detailModalItem.custom_notes}
                    </div>
                  )}
                </div>
              )}

              {/* MODAL CONTENT: ASSESSMENT CURRICULUM */}
              {detailModalType === 'assessment' && (
                <div>
                  {detailModalItem.is_pending_approval && (
                    <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', padding: '12px 16px', borderRadius: 10, marginBottom: 16, color: '#92400e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>Menunggu Persetujuan Kepala Sekolah (Principal)</strong>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#b45309' }}>
                          Usulan silabus materi dan tata tertib seleksi diajukan oleh Admin Admissions.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="adm-ro-grid-2">
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Jenjang Pendidikan</span>
                      <span className="adm-ro-val">{detailModalItem.level_name}</span>
                    </div>
                    <div className="adm-ro-field">
                      <span className="adm-ro-label">Judul Asesmen / Sesi Observasi Resmi</span>
                      <span className="adm-ro-val">{detailModalItem.assessment_title || '-'}</span>
                    </div>
                    <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                      <span className="adm-ro-label">Materi / Subjek Penilaian</span>
                      <span className="adm-ro-val">{detailModalItem.assessment_subjects || '-'}</span>
                    </div>
                    <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                      <span className="adm-ro-label">Deskripsi Sesi Observasi Masuk</span>
                      <span className="adm-ro-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {detailModalItem.assessment_description || '-'}
                      </span>
                    </div>
                    <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                      <span className="adm-ro-label">Perlengkapan yang Wajib Dibawa Siswa</span>
                      <span className="adm-ro-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {detailModalItem.assessment_preparation || '-'}
                      </span>
                    </div>
                    <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                      <span className="adm-ro-label">Tata Tertib &amp; Ketentuan Peserta Tes</span>
                      <span className="adm-ro-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {detailModalItem.assessment_rules || '-'}
                      </span>
                    </div>
                    <div className="adm-ro-field" style={{ gridColumn: 'span 2' }}>
                      <span className="adm-ro-label">Catatan Khusus Orang Tua / Pendamping</span>
                      <span className="adm-ro-val" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {detailModalItem.parent_instructions || '-'}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="adm-btn-action-view"
                      style={{ background: '#f5f3ff', color: '#7c3aed', borderColor: '#ddd6fe', padding: '8px 14px' }}
                      onClick={() => handlePreviewPass(detailModalItem)}
                    >
                      <FileText size={15} />
                      <span>Buka Pratinjau Kartu Peserta Tes (PDF)</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* MODAL FOOTER */}
            <div className="adm-config-modal-footer">
              <button
                type="button"
                className="adm-btn-secondary"
                onClick={() => setDetailModalItem(null)}
              >
                Tutup
              </button>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {(detailModalItem.is_pending_approval || (detailModalType === 'level' && (detailModalItem.fee_approval_status === 'pending_director' || Boolean(detailModalItem.pending_fee_payload)))) ? (
                  <>
                    <button
                      type="button"
                      className="adm-btn-danger"
                      disabled={actionLoading}
                      onClick={() => {
                        const itm = detailModalItem;
                        setDetailModalItem(null);
                        handleOpenRejectModal(itm, activeCategory);
                      }}
                    >
                      <XCircle size={15} />
                      <span>Tolak Pengajuan</span>
                    </button>
                    {detailModalType === 'period' && detailModalItem.action_type === 'delete' && (() => {
                      const appsCount = (applications || []).filter(a => String(a.period_id) === String(detailModalItem.id)).length;
                      return appsCount > 0;
                    })() ? (
                      <span style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fffbeb', border: '1px solid #fde68a', padding: '6px 12px', borderRadius: 6 }}>
                        <AlertTriangle size={15} />
                        <span>Penghapusan diblokir (terdapat pendaftar aktif)</span>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="adm-btn-approve-primary"
                        disabled={actionLoading}
                        onClick={() => {
                          const itm = detailModalItem;
                          setDetailModalItem(null);
                          handleDirectApprove(itm, activeCategory);
                        }}
                      >
                        <CheckCircle2 size={15} />
                        <span>Setujui (Approve)</span>
                      </button>
                    )}
                  </>
                ) : (
                  <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle2 size={16} />
                    <span>Konfigurasi Resmi Disetujui &amp; Aktif</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL FOR LEVEL FEE */}
      {rejectModalLevel && (
        <div className="afa-modal-overlay" onClick={() => setRejectModalLevel(null)}>
          <div className="afa-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="afa-modal-head">
              <h3>Tolak Pengajuan Biaya: {rejectModalLevel.level_name}</h3>
              <button type="button" className="afa-modal-close" onClick={() => setRejectModalLevel(null)}>
                <XCircle size={18} />
              </button>
            </div>
            <form onSubmit={handleConfirmRejectLevel} className="afa-modal-body">
              <p>
                Berikan catatan revisi atau alasan penolakan agar Admin Admissions dapat menyesuaikan nominal biaya sesuai instruksi Direktur Sekolah:
              </p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                required
                placeholder="Contoh: Nominal SPP terlalu tinggi dibandingkan tahun ajaran lalu, mohon diskusikan kembali..."
                className="afa-modal-textarea"
              />
              <div className="afa-modal-actions">
                <button
                  type="button"
                  className="afa-btn-secondary"
                  onClick={() => setRejectModalLevel(null)}
                  disabled={actionLoading}
                >
                  Batal
                </button>
                <button type="submit" className="afa-btn-danger" disabled={actionLoading}>
                  {actionLoading ? 'Menyimpan...' : 'Konfirmasi Penolakan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REJECT MODAL FOR CONFIGS */}
      {rejectModalConfig && (
        <div className="afa-modal-overlay" onClick={() => setRejectModalConfig(null)}>
          <div className="afa-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="afa-modal-head">
              <h3>Tolak Pengajuan: {rejectModalConfig.target_label || rejectModalConfig.period_name || rejectModalConfig.promo_code || rejectModalConfig.session_name}</h3>
              <button type="button" className="afa-modal-close" onClick={() => setRejectModalConfig(null)}>
                <XCircle size={18} />
              </button>
            </div>
            <form onSubmit={handleConfirmRejectConfig} className="afa-modal-body">
              <p>
                Berikan instruksi revisi atau alasan penolakan untuk Admin Admissions:
              </p>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={4}
                required
                placeholder="Contoh: Kuota Primary terlalu sedikit, mohon ditambah menjadi 60 siswa..."
                className="afa-modal-textarea"
              />
              <div className="afa-modal-actions">
                <button
                  type="button"
                  className="afa-btn-secondary"
                  onClick={() => setRejectModalConfig(null)}
                  disabled={actionLoading}
                >
                  Batal
                </button>
                <button type="submit" className="afa-btn-danger" disabled={actionLoading}>
                  {actionLoading ? 'Menyimpan...' : 'Konfirmasi Penolakan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EXAMINATION PASS PREVIEW MODAL */}
      {passPreviewModal && (
        <ExaminationPassModal
          app={passPreviewModal}
          onClose={() => setPassPreviewModal(null)}
        />
      )}
    </div>
  );
}
